import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import BottomNav from "../components/BottomNav";
import {
  DollarSign, Shield, FileText, TrendingUp,
  ChevronRight, AlertTriangle, CheckCircle, Clock,
} from "lucide-react";

export default function BillingDashboard() {
  const navigate = useNavigate();
  const { profile, user } = useAuth();

  const displayName = profile?.displayName || user?.displayName || "Billing";
  const firstName = displayName.split(" ")[0];

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const C = {
    card: {
      background: "rgba(15,23,42,0.7)",
      border: "1px solid rgba(99,179,237,0.08)",
      borderRadius: 16,
      padding: 20,
      marginBottom: 12,
    },
  };

  const quickActions = [
    {
      label: "Claims Queue",
      icon: DollarSign,
      color: "#4fd1c5",
      path: "/billing-dashboard?tab=claims",
      badge: "Coming Soon",
      disabled: true,
    },
    {
      label: "Authorizations",
      icon: Shield,
      color: "#b794f4",
      path: "/billing-dashboard?tab=auths",
      badge: "Coming Soon",
      disabled: true,
    },
    {
      label: "Billable Notes",
      icon: FileText,
      color: "#63b3ed",
      path: "/notes",
    },
    {
      label: "Revenue Reports",
      icon: TrendingUp,
      color: "#68d391",
      path: "/billing-dashboard?tab=reports",
      badge: "Coming Soon",
      disabled: true,
    },
  ];

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0f1a",
      color: "#e2e8f0",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      paddingBottom: 100,
    }}>
      {/* Header */}
      <header style={{
        padding: "20px 20px 16px",
        background: "linear-gradient(135deg, rgba(15,23,42,0.9), rgba(16,28,50,0.9))",
        borderBottom: "1px solid rgba(99,179,237,0.08)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, color: "#718096", fontWeight: 600 }}>{greeting()}</div>
            <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{firstName}</div>
            <div style={{
              fontSize: 10, fontWeight: 700, color: "#4fd1c5",
              letterSpacing: 1.5, textTransform: "uppercase", marginTop: 4,
            }}>
              Billing Dashboard
            </div>
          </div>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: "linear-gradient(135deg, #4fd1c5, #319795)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 900, color: "#0a0f1e",
          }}>
            {firstName[0]}
          </div>
        </div>
      </header>

      <main style={{ padding: 20 }}>
        {/* Stats Placeholder */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Pending", value: "—", color: "#ecc94b", sub: "claims" },
            { label: "Approved", value: "—", color: "#68d391", sub: "this week" },
            { label: "Denied", value: "—", color: "#fc8181", sub: "to review" },
          ].map((s, i) => (
            <div key={i} style={{ ...C.card, textAlign: "center", padding: 16 }}>
              <div style={{ fontSize: 24, fontWeight: 900, color: s.color, fontFamily: "monospace" }}>{s.value}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div style={{ fontSize: 12, fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 10 }}>
          Quick Actions
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {quickActions.map((action, i) => {
            const Icon = action.icon;
            return (
              <button
                key={i}
                onClick={() => !action.disabled && navigate(action.path)}
                style={{
                  ...C.card,
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  cursor: action.disabled ? "default" : "pointer",
                  opacity: action.disabled ? 0.5 : 1,
                  marginBottom: 0,
                  textAlign: "left",
                  width: "100%",
                }}
              >
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  background: `${action.color}12`,
                  border: `1px solid ${action.color}25`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Icon size={20} style={{ color: action.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#e2e8f0" }}>{action.label}</div>
                  {action.badge && (
                    <span style={{
                      fontSize: 9, fontWeight: 700, color: "#4fd1c5",
                      background: "rgba(79,209,197,0.1)",
                      padding: "2px 8px", borderRadius: 6,
                    }}>{action.badge}</span>
                  )}
                </div>
                <ChevronRight size={16} style={{ color: "#4a5568" }} />
              </button>
            );
          })}
        </div>

        {/* Info Card */}
        <div style={{
          ...C.card,
          marginTop: 20,
          background: "rgba(79,209,197,0.03)",
          border: "1px solid rgba(79,209,197,0.1)",
        }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#4fd1c5", marginBottom: 6 }}>
            Billing Features Coming Soon
          </div>
          <div style={{ fontSize: 12, color: "#718096", lineHeight: 1.6 }}>
            Claims management, authorization tracking, denial management, and revenue cycle
            reporting are being built in Tracks 6 & 7. For now, you can view billable notes.
          </div>
        </div>
      </main>

      <BottomNav active="home" />
    </div>
  );
}